import { apiClient } from '@littlebird/auth-client';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const ActivitySnapshotSchema = z.object({
  id: z.number(),
  uuid: z.string().nullable(),
  app: z.string().nullable(),
  kind: z.string().nullable(),
  captured_at: z.string(),
  ck_job_id: z.string().nullable(),
  focus_interval_id: z.string().nullable(),
  device_id: z.string().nullable(),
  title: z.string().nullable(),
  url: z.string().nullable(),
});

const ActivityIntervalSchema = z.object({
  id: z.string(),
  type: z.enum(['observation', 'presence', 'focus']),
  device_id: z.string(),
  start: z.string(),
  end: z.string().nullable(),
  open_reason: z.string(),
  close_reason: z.string().nullable(),
  heartbeat_at: z.string().nullable(),
  tz_offset_minutes: z.number().nullable(),
  capabilities: z.array(z.string()).nullable(),
  bundle_identifier: z.string().nullable(),
  title: z.string().nullable(),
  url: z.string().nullable(),
  hidden_by: z.string().nullable(),
});

const ActivityTimelineResponseSchema = z.object({
  snapshots: z.array(ActivitySnapshotSchema),
  intervals: z.array(ActivityIntervalSchema),
  truncated: z.boolean(),
});

const ActivitySnapshotContentSchema = z.object({
  id: z.number(),
  text: z.string(),
});

export type ActivitySnapshot = {
  id: number;
  uuid: string | null;
  app: string | null;
  kind: string | null;
  capturedAt: string;
  ckJobId: string | null;
  focusIntervalId: string | null;
  deviceId: string | null;
  title: string | null;
  url: string | null;
};

export type IntervalType = 'observation' | 'presence' | 'focus';

export type ActivityInterval = {
  id: string;
  type: IntervalType;
  deviceId: string;
  start: string;
  end: string | null;
  openReason: string;
  closeReason: string | null;
  /** Observation only: the last heartbeat, and the environment the observer ran in */
  heartbeatAt: string | null;
  tzOffsetMinutes: number | null;
  capabilities: string[] | null;
  /** Focus only: what the observer recorded of the focused window, with any hidden part null */
  bundleIdentifier: string | null;
  title: string | null;
  url: string | null;
  hiddenBy: string | null;
};

export type ActivityTimelineDay = {
  snapshots: ActivitySnapshot[];
  intervals: ActivityInterval[];
  truncated: boolean;
};

/**
 * The window is sent as absolute instants rather than a calendar day so the day boundaries
 * stay the ones the user sees locally, and the server needs no timezone of its own.
 */
export const useActivityTimelineQuery = (dayStart: Date, dayEnd: Date) =>
  useQuery({
    queryKey: ['activityTimeline', dayStart.toISOString()],
    staleTime: 60 * 1000,
    queryFn: async ({ signal }): Promise<ActivityTimelineDay> => {
      const response = await apiClient.get('/debug/activity_timeline/', {
        params: { start: dayStart.toISOString(), end: dayEnd.toISOString() },
        signal,
      });
      const { snapshots, intervals, truncated } =
        await ActivityTimelineResponseSchema.parseAsync(response);

      return {
        truncated,
        intervals: intervals.map((interval) => ({
          id: interval.id,
          type: interval.type,
          deviceId: interval.device_id,
          start: interval.start,
          end: interval.end,
          openReason: interval.open_reason,
          closeReason: interval.close_reason,
          heartbeatAt: interval.heartbeat_at,
          tzOffsetMinutes: interval.tz_offset_minutes,
          capabilities: interval.capabilities,
          bundleIdentifier: interval.bundle_identifier,
          title: interval.title,
          url: interval.url,
          hiddenBy: interval.hidden_by,
        })),
        snapshots: snapshots.map((snapshot) => ({
          id: snapshot.id,
          uuid: snapshot.uuid,
          app: snapshot.app,
          kind: snapshot.kind,
          capturedAt: snapshot.captured_at,
          ckJobId: snapshot.ck_job_id,
          focusIntervalId: snapshot.focus_interval_id,
          deviceId: snapshot.device_id,
          title: snapshot.title,
          url: snapshot.url,
        })),
      };
    },
    // Ensures intervals that the backend closes later still reach the screen, by polling a day
    // until a fetch finishes after the day ended. This property stays after `queryFn`, because
    // TypeScript cannot infer the data type when a callback that reads `query` comes before it
    refetchInterval: (query) =>
      query.state.dataUpdatedAt < dayEnd.getTime() ? 60_000 : false,
  });

/**
 * One snapshot's full text, fetched only once the detail pane opens it.
 *
 * The day query deliberately carries no capture text, so it never drags every capture across the
 * wire, and the pane pays for the whole capture one row at a time.
 */
export const useActivitySnapshotContentQuery = (snapshotId: number | null) =>
  useQuery({
    queryKey: ['activityTimelineContent', snapshotId],
    enabled: snapshotId !== null,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }): Promise<string> => {
      const response = await apiClient.get(
        `/debug/activity_timeline/${snapshotId}/content/`,
        { signal }
      );
      const { text } = await ActivitySnapshotContentSchema.parseAsync(response);

      return text;
    },
  });
